import type { ComplianceQueue, ComplianceQueueEntry } from '@homecare/contracts';
import { hoursText } from '@homecare/domain';
import { Card, EmptyState, Eyebrow, Notice, PageHeader, Spinner, StatusPill } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';

/**
 * The compliance queue — HR's morning screen (slice-02 session 8).
 *
 * ## What it answers
 *
 * "Who cannot work, and what does that break." Treated the way the coverage
 * panel was treated: not a table of credentials with nicer borders, which
 * would ask HR to work out from expiry dates and a rota who matters most.
 * Each row says who, what stops them, what their absence costs, and why they
 * are where they are.
 *
 * ## It renders the server's order and the server's words
 *
 * The order is decided by `rankComplianceQueue` in `@homecare/domain`, and
 * the sentence saying why each row is above the next is composed beside the
 * comparator that decided it. This screen does neither: it does not sort,
 * and it does not write the explanation. A client that re-sorted, or that
 * composed its own reason, could show an order and a reason that disagree —
 * and neither half would look wrong on its own. `compliance-queue.spec.tsx`
 * holds it to that.
 *
 * Dates are shown as the server sends them, because the explanations quote
 * them that way; a row that said "9 Oct" beside a sentence saying
 * "2026-10-09" would be two formats for one fact. Formatting them here would
 * also be formatting them in the browser's timezone, which is the agency's
 * only by accident (R-1e).
 */
export function ComplianceQueuePage(): ReactNode {
  const api = useApi();

  const queue = useQuery({
    queryKey: ['credentials', 'compliance-queue'],
    queryFn: () => api.get<ComplianceQueue>('/v1/compliance-queue'),
    staleTime: 0,
  });

  const data = queue.data;

  return (
    <section className="grid gap-6">
      <PageHeader
        title="Credentials"
        meta="Who cannot work, and what that breaks — most urgent first."
      />

      <ErrorNote error={queue.error} nextStep="Reload the page to try again." />

      {queue.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Working out who cannot work…
        </p>
      ) : data ? (
        <>
          {/* The basis, said plainly, so nobody reads "hours at stake" as
              booked visits. There are no visits yet; when there are, the
              API's `basis` changes and so does this sentence. */}
          <Notice
            tone="scheduled"
            title={`As of ${data.asOfDate}, looking at ${data.horizon.from} to ${data.horizon.to}.`}
          >
            Work at stake counts the hours each caregiver has offered the rota in that time, less
            approved time off. It is not booked visits: there is no schedule yet.
          </Notice>

          {data.entries.length === 0 ? (
            <Card>
              <EmptyState
                title="Everyone can work"
                explanation="No active caregiver is missing a credential they need, and none runs out inside its warning window. Anyone who stops being able to work appears here, with what it breaks."
              />
            </Card>
          ) : (
            <ol className="grid gap-3" aria-label="Caregivers who cannot work, most urgent first">
              {data.entries.map((entry) => (
                <QueueRow key={entry.caregiverId} entry={entry} />
              ))}
            </ol>
          )}
        </>
      ) : null}
    </section>
  );
}

function QueueRow({ entry }: { entry: ComplianceQueueEntry }): ReactNode {
  return (
    <li>
      <Card>
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="grid content-start gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-ink-3 w-6 text-sm font-semibold" data-numeric>
                {entry.rank}
              </span>
              {entry.caregiverName === null ? (
                <span className="text-ink text-md font-semibold">
                  A caregiver you don’t have access to
                </span>
              ) : (
                <Link
                  to="/caregivers/$caregiverId"
                  params={{ caregiverId: entry.caregiverId }}
                  className="text-ink text-md hover:text-brand font-semibold underline-offset-2 hover:underline"
                >
                  {entry.caregiverName}
                </Link>
              )}
              <StatusPill tone={entry.cannotWorkToday ? 'critical' : 'warning'}>
                {entry.cannotWorkToday
                  ? 'Cannot work today'
                  : `Cannot work from ${entry.blockedFrom}`}
              </StatusPill>
              {/* Onboarding with the checklist clear: the credential below is
                  the one thing between this person and the rota. */}
              {entry.caregiverStatus === 'ONBOARDING' && (
                <StatusPill tone="progress">Onboarding: only this left</StatusPill>
              )}
            </div>
            <ul className="text-ink-2 grid gap-1 pl-8 text-sm">
              {entry.issues.map((issue) => (
                <li key={`${issue.credentialTypeId}-${issue.reason}`}>{issue.message}</li>
              ))}
            </ul>
          </div>

          <div className="grid content-start gap-3 text-sm">
            <div>
              <Eyebrow>What it breaks</Eyebrow>
              <p className="text-ink mt-1">{entry.explanation.atStake}</p>
              {entry.workAtStake.days.length > 0 && (
                <ul
                  className="text-ink-3 mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs"
                  aria-label="Days at stake"
                >
                  {entry.workAtStake.days.map((day) => (
                    <li key={day.date} data-numeric>
                      {day.date} · {hoursText(day.minutes)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Eyebrow>Why it is here</Eyebrow>
              <p className="text-ink-2 mt-1" data-placement>
                {entry.explanation.placement}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </li>
  );
}
